import { useState } from "react";
import { Link } from "react-router-dom";
import { Volume2, ChevronRight } from "lucide-react";

/**
 * Dictionary-style term card. Mirrors the Merriam-Webster reference layout
 * the admin shared: serif term, muted part-of-speech, pronunciation pill,
 * expandable synonyms link, and a numbered definition rail. Every optional
 * field (POS, pronunciation, hospice context, usage caution, see-also) is
 * only rendered when present so legacy rows still look right.
 */
export default function TermCard({ term, focused, testId }) {
  const [showAliases, setShowAliases] = useState(false);
  const defs = (term.definitions && term.definitions.length > 0)
    ? term.definitions
    : ((term.definition || "").trim() ? [term.definition.trim()] : []);
  const aliases = term.aliases || [];
  const seeAlso = term.see_also || [];
  const hasSpeech = !!term.pronunciation && "speechSynthesis" in window;

  const speak = () => {
    if (!hasSpeech) return;
    try {
      const u = new SpeechSynthesisUtterance(term.term);
      u.rate = 0.9;
      window.speechSynthesis.speak(u);
    } catch (_) { /* silent */ }
  };

  return (
    <article
      data-testid={testId}
      className={`relative py-6 pl-6 pr-2 ${focused ? "bg-sky-50/60 dark:bg-sky-950/20" : ""}`}
    >
      {/* Term + part of speech */}
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-heading text-4xl md:text-5xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none">
          {term.term.toLowerCase()}
        </h2>
        {term.part_of_speech && (
          <span className="text-slate-400 dark:text-slate-500 italic text-2xl md:text-3xl font-light leading-none">
            {term.part_of_speech}
          </span>
        )}
      </div>

      {/* Syllable / pronunciation row */}
      <div className="mt-3 flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm flex-wrap">
        <span className="tracking-wide">{syllabify(term.term)}</span>
        {term.pronunciation && (
          <button
            onClick={speak}
            disabled={!hasSpeech}
            data-testid={`glossary-pronounce-${term.term_id}`}
            className="inline-flex items-center gap-1.5 border border-slate-300 dark:border-slate-700 rounded-full px-3 py-0.5 hover:border-slate-500 dark:hover:border-slate-500 disabled:cursor-default disabled:hover:border-slate-300 transition-colors"
            title={hasSpeech ? `Pronounce ${term.term}` : term.pronunciation}
          >
            <span className="font-mono text-xs">ˈ{term.pronunciation}</span>
            <Volume2 className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>

      {/* Synonyms of X ▸ (expandable) */}
      {aliases.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowAliases(v => !v)}
            data-testid={`glossary-synonyms-toggle-${term.term_id}`}
            className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400 text-sm italic hover:text-sky-900 dark:hover:text-sky-300"
          >
            Synonyms of <em className="not-italic font-medium">{term.term.toLowerCase()}</em>
            <ChevronRight className={`w-4 h-4 transition-transform ${showAliases ? "rotate-90" : ""}`}/>
          </button>
          {showAliases && (
            <div
              data-testid={`glossary-synonyms-list-${term.term_id}`}
              className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed"
            >
              {aliases.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Numbered definitions with left rail */}
      {defs.length > 0 && (
        <ol className="mt-6 space-y-6">
          {defs.map((d, i) => (
            <li
              key={i}
              data-testid={`glossary-definition-${term.term_id}-${i + 1}`}
              className="grid grid-cols-[auto_1fr] gap-x-4 relative"
            >
              <span className="font-heading text-slate-800 dark:text-slate-200 font-bold text-lg leading-snug pt-1">{i + 1}</span>
              <div className="border-l border-slate-300 dark:border-slate-700 pl-4">
                <p className="text-slate-800 dark:text-slate-200 leading-relaxed">
                  <span className="text-slate-400 dark:text-slate-500">:</span> {d}
                </p>
                {i === defs.length - 1 && term.hospice_context && (
                  <p className="mt-3 text-slate-700 dark:text-slate-300 leading-relaxed">
                    <em className="text-slate-500 dark:text-slate-400">also</em>
                    <span className="text-slate-400 dark:text-slate-500"> : </span>
                    {term.hospice_context}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {defs.length === 0 && (
        <p className="mt-6 text-slate-500 italic text-sm">No definition yet.</p>
      )}

      {/* Usage / caution */}
      {term.usage_caution && (
        <div className="mt-5 pl-6 border-l-2 border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 rounded-r py-2 pr-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-700 dark:text-amber-500 mb-1">Usage note</div>
          <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed italic">{term.usage_caution}</div>
        </div>
      )}

      {/* See also */}
      {seeAlso.length > 0 && (
        <div className="mt-5 flex items-center gap-2 flex-wrap text-xs">
          <span className="uppercase tracking-widest text-slate-500 dark:text-slate-400">See also</span>
          {seeAlso.map((s, i) => (
            <Link
              key={i}
              to={`/glossary?q=${encodeURIComponent(s)}`}
              data-testid={`glossary-see-also-${term.term_id}-${i}`}
              className="inline-block px-2.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-sky-500 hover:text-sky-700 dark:hover:text-sky-400 transition-colors"
            >
              {s}
            </Link>
          ))}
        </div>
      )}
    </article>
  );
}

/** Very small heuristic to render `hos·pice` style syllabification. If the
 * term is a single word ≤10 chars, we insert middle dots at vowel boundaries;
 * for anything more complex we just fall back to the term itself. */
function syllabify(text) {
  const raw = String(text || "").toLowerCase();
  if (!raw || raw.includes(" ") || raw.length > 10) return raw;
  // Break between a vowel cluster and the following consonant → next cluster.
  const parts = raw.match(/[^aeiouy]*[aeiouy]+(?:[^aeiouy](?=[aeiouy]))?/gi) || [raw];
  const joined = parts.filter(Boolean).join("·");
  return joined || raw;
}
